import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ContinueAsHeader from '../components/ContinueAsHeader';
import ButtonLink from '../components/ButtonLink';
import TallButton from '../components/TallButton';
import Account from '../components/Account';
import styled from '@emotion/styled';
import { OidcDisplay } from '../types';
import { isOidcDisplayMode } from '../utilities/oidc/oidc';
import WhiteboxPage from '../components/WhiteboxPage';
import { gridSize } from '@atlaskit/theme';
import {
  useButtonClickedEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';

const AccountWrapper = styled.div`
  .account-entry {
    padding-bottom: ${gridSize() * 2}px;
    border-bottom: none;
  }
`;

const pageId = 'continueAsScreen';

export async function continueAsLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/login/continue-as/state', request, {
    capabilities: ['capability-group--login', 'capability-name--continue-as'],
  });
}

export const ContinueAsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { avatarUrl, continueUrl, displayName, email } = useAppSelector(state => state.continueAs);
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const navigate = useNavigateSoftOrHard();

  const location = useLocation();

  usePageViewedEvent(pageId);

  const trackContinueButtonClicked = useButtonClickedEvent(pageId, 'continueAsButton');
  const handleContinueButton = useCallback(() => {
    trackContinueButtonClicked();
    setIsLoading(true);
    navigate(continueUrl);
  }, [continueUrl, trackContinueButtonClicked, navigate]);

  const trackLoginWithOtherAccountClicked = useButtonClickedEvent(pageId, 'loginWithOtherAccount');
  const whiteboxFooter = () => {
    const operand = location.search && location.search.length > 0 ? '&' : '?';
    const loginLink = `/login${location.search}${operand}prompt=true`;
    if (!isOidcDisplayMode(oidcContext, OidcDisplay.Continue)) {
      return (
        <>
          <ButtonLink onClick={trackLoginWithOtherAccountClicked} href={loginLink}>
            <FormattedMessage
              id="continue.as.login.with.another.account"
              defaultMessage="Login with a different account"
            />
          </ButtonLink>
        </>
      );
    }

    return null;
  };

  return (
    <WhiteboxPage
      pageId={pageId}
      header={<ContinueAsHeader displayName={displayName} />}
      whiteboxFooter={whiteboxFooter()}>
      <AccountWrapper>
        <Account primary={true} avatarUrl={avatarUrl} displayName={displayName} email={email} />
      </AccountWrapper>
      <TallButton
        appearance="primary"
        id="login-submit"
        type="submit"
        isFullWidth
        isLoading={isLoading}
        onClick={handleContinueButton}>
        <FormattedMessage id="continue.as.button" defaultMessage="Continue" />
      </TallButton>
    </WhiteboxPage>
  );
};

export default ContinueAsPage;
